import { MissingNumberOptions, Operations, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Stamps each math-fact problem with which operand is left blank, from the
 * MissingNumber setting.
 *
 * Runs after GenerateDataV2 has built and sliced the pages, not inside it: the
 * arithmetic is unchanged, only which cell renders blank. A missing-number
 * problem is byte-for-byte an existing fact - see ADR 0005 for why this is a
 * setting rather than a seventh format.
 *
 * `Off` returns the pages untouched and by reference, so an ordinary sheet is
 * provably unchanged. Long division is skipped whole: its bracket layout
 * (Problems-UI/MathFacts.tsx) reads `numbers` directly and has nowhere to put a
 * blank operand, so a sheet that involves it is left alone (ADR 0005 s5).
 */
export function applyMissingNumbers(
    pages: ProblemData[][],
    settings: ISettingsProps
): ProblemData[][] {
    const setting = settings.MissingNumber;
    if (!setting || setting === MissingNumberOptions.Off) {
        return pages;
    }
    if (settings.Operators?.some((operator) => operator.value === Operations.LongDivision)) {
        return pages;
    }

    // Mixed alternates 0, 1, 0, 1 by position rather than drawing randomly, so a
    // sheet cannot come out all-firsts by chance. The counter runs across pages
    // so the alternation does not reset at each page break.
    let index = 0;
    return pages.map((page) =>
        page.map((problem) => {
            const hidden = hiddenSlotFor(setting, index);
            index++;
            return { ...problem, hidden };
        })
    );
}

/** Which operand (0 or 1) this problem hides. Never the answer - ADR 0005 s4. */
function hiddenSlotFor(setting: MissingNumberOptions, index: number): 0 | 1 {
    switch (setting) {
        case MissingNumberOptions.FirstNumber:
            return 0;
        case MissingNumberOptions.SecondNumber:
            return 1;
        case MissingNumberOptions.Mixed:
        default:
            return (index % 2) as 0 | 1;
    }
}
